/**
 * What the device keeps when there is no signal.
 *
 * <p>IndexedDB rather than localStorage: a photograph is two to five megabytes and
 * localStorage holds five in total, synchronously, on the main thread. The first photograph
 * would fill it and the second would freeze the screen.</p>
 *
 * <p>Two stores. <b>audits</b> is the last thing the server said, whole. <b>outbox</b> is
 * what has happened since, in order. The screen renders the first with the second folded
 * in, so there is no third state to keep consistent and nothing to merge — the server's
 * answer always replaces the cache outright.</p>
 */
import type { AuditDetail, SaveAnswer } from '../api/audits';

const DATABASE = 'denetim';
const VERSION = 1;

export type PendingOp =
  | { kind: 'answer'; questionId: string; body: SaveAnswer }
  | { kind: 'notes'; notes: string | null }
  | { kind: 'photo'; questionId: string; blob: Blob; name: string; type: string }
  | { kind: 'removePhoto'; photoId: string }
  | { kind: 'sign'; blob: Blob; name: string }
  | { kind: 'removeSignature' }
  | { kind: 'submit' };

export interface Pending {
  /** Assigned here, so an operation keeps its place in the queue across restarts. */
  id: number;
  auditId: string;
  at: number;
  attempts: number;
  lastError?: string;
  op: PendingOp;
}

/**
 * The connection, opened once and kept — until it dies.
 *
 * <p><b>It used to be kept for ever, and one bad video stopped everything after it.</b>
 * Chrome closes a connection after an internal failure, such as a blob it could not write.
 * The module went on handing out the dead one: every later write — the next photograph, the
 * next answer — and every read of the queue threw <code>InvalidStateError</code> until the
 * page was reloaded. On a phone that read as "after the video failed, photos would not
 * upload either". Measured in Chromium with the held connection made to behave as closed:
 * the first add went in, the next two and the queue read all failed.</p>
 *
 * <p>So a connection that closes is forgotten, and the next call opens a fresh one.</p>
 */
let opening: Promise<IDBDatabase> | null = null;

/** The connection {@link opening} resolved to, so it can be let go of without waiting. */
let held: IDBDatabase | null = null;

/** Lets go of this connection, if it is still the one being handed out. */
function forget(db: IDBDatabase) {
  if (held !== db) return;
  held = null;
  opening = null;
}

function open(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains('audits')) db.createObjectStore('audits', { keyPath: 'id' });

      if (!db.objectStoreNames.contains('outbox')) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
        outbox.createIndex('auditId', 'auditId');
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      held = db;
      db.onclose = () => forget(db);
      resolve(db);
    };

    // A failed open is not kept either: the next call tries again.
    request.onerror = () => {
      opening = null;
      reject(request.error);
    };
  });

  return opening;
}

/** What a connection that has been closed under us throws when asked for a transaction. */
const closed = (error: unknown) => error instanceof DOMException && error.name === 'InvalidStateError';

/**
 * One request in its own transaction, settled when the transaction is — not the request.
 *
 * <p><b>It used to settle on the request, and a photograph disappeared without a word.</b>
 * A blob is copied into the database at commit, after its request has already reported
 * success. A file from an Android gallery that cannot be read at that moment aborts the
 * transaction, and by then the add had resolved, the screen had moved on and nothing was
 * listening: no tile, no warning, no queue entry. Measured in Chromium with a commit made to
 * fail after success — the add returned an id and the queue held nothing.</p>
 */
function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
  retried = false,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        let transaction: IDBTransaction;

        try {
          transaction = db.transaction(store, mode);
        } catch (error) {
          // Closed without saying so — close() fires no event. Nothing was started, so one
          // more go on a fresh connection is safe.
          if (!retried && closed(error)) {
            forget(db);
            resolve(run(store, mode, work, true));
            return;
          }

          reject(error);
          return;
        }

        const request = work(transaction.objectStore(store));

        transaction.oncomplete = () => resolve(request.result);
        transaction.onabort = () =>
          reject(transaction.error ?? request.error ?? new Error('The write was not committed.'));
        transaction.onerror = () => reject(transaction.error ?? request.error);
      }),
  );
}

export const cache = {
  /** The server's last word on this audit. Replaced outright, never patched. */
  put: (detail: AuditDetail) =>
    run('audits', 'readwrite', (s) => s.put({ id: detail.summary.id, detail })),

  get: async (id: string): Promise<AuditDetail | null> => {
    const row = await run<{ detail: AuditDetail } | undefined>('audits', 'readonly', (s) => s.get(id));
    return row?.detail ?? null;
  },

  all: async (): Promise<AuditDetail[]> => {
    const rows = await run<{ detail: AuditDetail }[]>('audits', 'readonly', (s) => s.getAll());
    return rows.map((r) => r.detail);
  },

  forget: (id: string) => run('audits', 'readwrite', (s) => s.delete(id)),
};

export const outbox = {
  add: (auditId: string, op: PendingOp) =>
    run<IDBValidKey>('outbox', 'readwrite', (s) =>
      s.add({ auditId, at: Date.now(), attempts: 0, op } as Omit<Pending, 'id'>),
    ),

  /** Everything waiting, oldest first — which is the order it has to be replayed in. */
  all: async (): Promise<Pending[]> => {
    const rows = await run<Pending[]>('outbox', 'readonly', (s) => s.getAll());
    return rows.sort((a, b) => a.id - b.id);
  },

  forAudit: async (auditId: string): Promise<Pending[]> =>
    (await outbox.all()).filter((p) => p.auditId === auditId),

  done: (id: number) => run('outbox', 'readwrite', (s) => s.delete(id)),

  /**
   * Drops everything still queued for one audit.
   *
   * <p>For an audit that no longer exists. Without this the queue would keep trying to put
   * answers on a draft the server has thrown away, and every one of them would come back
   * refused and be reported to the auditor as a failure — for a visit they deliberately
   * abandoned.</p>
   */
  forget: async (auditId: string): Promise<number> => {
    const queued = await outbox.forAudit(auditId);
    for (const pending of queued) await outbox.done(pending.id);
    return queued.length;
  },

  /**
   * Records that an attempt failed, and why. Kept rather than dropped: the operation is
   * still owed, and an auditor who can see what is stuck can decide what to do about it.
   */
  failed: async (pending: Pending, reason: string) => {
    await run('outbox', 'readwrite', (s) =>
      s.put({ ...pending, attempts: pending.attempts + 1, lastError: reason }),
    );
  },
};

/** Whether anything is waiting to go up. The screen shows this and so does the list. */
export async function pendingCount(auditId?: string): Promise<number> {
  const all = auditId ? await outbox.forAudit(auditId) : await outbox.all();
  return all.length;
}
